import { useCallback, useMemo, useRef, useState } from 'react';
import type { PropsWithChildren } from 'react';
import type React from 'react';

import type { IAllProps as EditorProps } from '@hugerte/hugerte-react';
import { Editor } from '@hugerte/hugerte-react';
import type { Editor as EditorType } from 'hugerte';

import { ClientRender } from '@change-corgi/core-react/ssr/render';
import { VisuallyHidden } from '@change-corgi/design-system-legacy/a11y';
import { Box } from '@change-corgi/design-system-legacy/layout';
import { Text } from '@change-corgi/design-system-legacy/typography';

import localeMapping from 'config/hugerte/locale-mapping.json';

import { useLocale } from 'src/app/shared/hooks/l10n';

import { useDefaultConfig } from './hooks/useDefaultConfig';
import { hugerte } from './hugerte';
import { GlobalStyle } from './HugeRteStyling';
import { filePickerCallback } from './shared/config';

type EditorInitConfig = EditorProps['init'];

export type Props = {
	ariaLabel: string;
	customEditorConfig?: EditorInitConfig;
	descriptiveLabel: string;
	hideLabel?: boolean;
	textEditorId: string;
	initialValue?: string;
	onEditorChange?: (editorContent: string, editor: EditorType) => void;
	onEditorFocus?: () => void;
	onEditorInit?: (editor: EditorType) => void;
};

export const RichTextEditor: React.FC<PropsWithChildren<Props>> = ({
	ariaLabel,
	customEditorConfig,
	descriptiveLabel,
	hideLabel,
	textEditorId,
	initialValue,
	onEditorChange,
	onEditorFocus,
	onEditorInit,
}) => {
	const editorRef = useRef<EditorType | null>(null);
	const locale = useLocale();
	const localeCode = localeMapping.localeMapping[locale];

	const defaultConfig = useDefaultConfig();

	const editorConfig: EditorInitConfig = useMemo(
		() => ({
			...defaultConfig,
			...customEditorConfig,
			content_style: `${defaultConfig?.content_style || ''}\n${customEditorConfig?.content_style || ''}`,
			// eslint-disable-next-line react-hooks/refs
			file_picker_callback: filePickerCallback(editorRef),
		}),
		[defaultConfig, customEditorConfig],
	);

	const [editorText, setEditorText] = useState(initialValue ?? '');
	const [hasBeenInitialized, setHasBeenInitialized] = useState(false);

	const handleEditorChange = useCallback(
		(newValue: string, editor: EditorType) => {
			setEditorText(newValue);
			/*
				hugerte makes some formatting to the editor's content during initialization which triggers
				a change event. We want to set the editor as dirty only when the content has been changed by
				the user (usually after the editor is initialized)
			*/
			if (hasBeenInitialized) {
				editor.setDirty(true);
			}

			onEditorChange?.(newValue, editor);
		},
		[onEditorChange, hasBeenInitialized],
	);

	const handleEditorFocus = useCallback(() => {
		onEditorFocus?.();
	}, [onEditorFocus]);

	const handleEditorInit = useCallback(
		(_evt: unknown, editor: EditorType) => {
			editorRef.current = editor;
			hugerte.util.I18n.add(localeCode, { URL: 'URL' });
			onEditorInit?.(editor);
			setHasBeenInitialized(true);
		},
		[localeCode, onEditorInit],
	);

	const LabelWrapper = hideLabel ? VisuallyHidden : Box;
	const handleLabelClick = useCallback(() => {
		editorRef.current?.focus();
	}, [editorRef]);

	return (
		// SSR not supported by hugerte
		<ClientRender>
			<GlobalStyle />
			<LabelWrapper mb={4} onClick={handleLabelClick}>
				<label htmlFor={textEditorId}>
					<Text as="span" size="caption" sx={{ fontWeight: 'bold' }}>
						{descriptiveLabel}
					</Text>
				</label>
			</LabelWrapper>
			<Editor
				onInit={handleEditorInit}
				init={editorConfig}
				id={textEditorId}
				initialValue={initialValue}
				value={editorText}
				onEditorChange={handleEditorChange}
				onFocus={handleEditorFocus}
				data-testid={textEditorId}
				aria-label={ariaLabel}
			/>
		</ClientRender>
	);
};
